/*
 * A quantity, short enough to sit at the end of a row.
 *
 * THERE IS EXACTLY ONE OF THESE IN THE PRODUCT, and it counts open letters in a room
 * (docs/5 § D260805-1500). Nothing else in the app renders a tally: no likes, no
 * comments, no followers, no letters sent on, for anybody including the author
 * (docs/5 § D260801-1119). So this module is deliberately not called `formatNumber`
 * and does not live in `lib/utils` — a general-purpose number formatter is an
 * invitation, and the next caller that reaches for one is the caller that must not
 * exist. If a second use ever appears, the question to answer first is what it is
 * counting and whether the count is of a PLACE or of a PERSON.
 */

/**
 * `1`, `999`, `1.5k`, `12k`, `3.4m`.
 *
 * `Intl.NumberFormat` RATHER THAN DIVIDING BY A THOUSAND, and it is the difference
 * between a formatter and a bug in eleven languages. Compact notation is a CLDR
 * dataset: the abbreviation, whether there is a space before it, the decimal mark and
 * the digits themselves all vary — Arabic wants `١٫٥ ألف`, German writes `1,5 Tsd.`,
 * and hand-rolling `${(n / 1000).toFixed(1)}k` produces English abbreviations with a
 * full stop for a decimal comma in every one of them. The app is required to hold up
 * in RTL (CLAUDE.md), and RTL here is Arabic-Indic digits, not a mirrored `1.5k`.
 *
 * LOWER CASE IS APPLIED ONLY WHERE IT MEANS ANYTHING. English CLDR gives `1.5K`, and
 * the brief asked for `1.5k` — a lower-case suffix reads as a unit rather than as
 * shouting, which is what a muted 12px figure at the end of a row wants. `toLowerCase`
 * on the whole string is safe for the rest: scripts without case are unchanged by it,
 * and a language whose abbreviation is a word (`Tsd.`, `mil`) is left as CLDR has it
 * because `undefined` locale means the reader's own, and their own is not English.
 *
 * `maximumFractionDigits: 1` IS WHAT KEEPS IT SHORT. Without it CLDR rounds to
 * integers below 100k (`2K` for 2,400), which loses the distinction between a room
 * with two thousand letters and one with nearly three. With it the string is never
 * more than five characters wide, which is what the row was measured for.
 *
 * BELOW A THOUSAND IT IS THE NUMBER ITSELF, formatted for the locale but not
 * abbreviated: there is no shorter way to write 847, and `0.8k` is both longer and
 * a lie.
 */
export function formatCount(n: number): string {
  const safe = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  })
    .format(safe)
    .toLowerCase();
}
